import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import { ThemeContext } from '../../context/ThemeContext';
import { API_BASE } from '../../api';

function MyTasks() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState({});
  const { currentTheme } = useContext(ThemeContext);
  const navigate = useNavigate();


  // 🎨 GLOBAL COLORS DEFINITION
  const colors = {
    backgroundDark: '#0f0c29', 
    backgroundMedium: '#302b63', 
    backgroundLight: '#24243e',
    textLight: 'rgba(255, 255, 255, 0.8)',
    borderDark: 'rgba(255, 255, 255, 0.2)',
    primary: currentTheme?.primary || '#6366F1',
    secondary: currentTheme?.secondary || '#8B5CF6',
    accent: currentTheme?.accent || '#06B6D4',
    success: '#10B981',
    warning: '#F59E0B',
    danger: '#EF4444'
  };

  // Optimized Styles with Smaller Values
  const styles = {
    container: {
      minHeight: '100vh',
      background: `linear-gradient(135deg, ${colors.backgroundDark} 0%, ${colors.backgroundMedium} 50%, ${colors.backgroundLight} 100%)`,
      position: 'relative',
      overflowX: 'hidden'
    },
    header: {
      padding: '30px 20px 20px',
      position: 'relative',
      zIndex: 2
    },
    headerContent: {
      maxWidth: '1200px',
      margin: '0 auto'
    },
    greeting: {
      display: 'flex',
      alignItems: 'center',
      gap: '15px',
      marginBottom: '10px',
      flexWrap: 'wrap'
    },
    pageTitle: {
      fontSize: '2rem',
      fontWeight: '700',
      background: 'linear-gradient(135deg, #fff 0%, #a5b4fc 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      margin: 0
    },
    welcomeBadge: {
      background: 'rgba(255, 255, 255, 0.1)',
      padding: '6px 12px',
      borderRadius: '15px',
      border: `1px solid ${colors.borderDark}`,
      backdropFilter: 'blur(10px)',
      display: 'flex',
      alignItems: 'center',
      gap: '6px'
    },
    badgeText: {
      color: 'white',
      fontWeight: '600',
      fontSize: '0.8rem'
    },
    badgeRole: {
      background: colors.primary,
      color: 'white',
      padding: '2px 6px',
      borderRadius: '10px',
      fontSize: '0.65rem',
      fontWeight: '600',
      textTransform: 'uppercase'
    },
    pageSubtitle: {
      color: colors.textLight,
      fontSize: '0.9rem',
      margin: 0,
      maxWidth: '600px'
    },
    messageAlert: {
      color: 'white',
      padding: '12px 15px',
      borderRadius: '10px',
      margin: '0 20px 15px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      maxWidth: '1200px',
      marginLeft: 'auto',
      marginRight: 'auto',
      fontSize: '0.85rem'
    },
    content: {
      padding: '0 20px 25px',
      maxWidth: '1200px',
      margin: '0 auto',
      position: 'relative',
      zIndex: 2
    },
    formCard: {
      background: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '15px',
      border: `1px solid ${colors.borderDark}`,
      backdropFilter: 'blur(10px)',
      overflow: 'hidden',
      transition: 'all 0.3s ease',
      boxShadow: '0 5px 15px rgba(0, 0, 0, 0.2)'
    },
    cardHeader: {
      background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
      color: 'white',
      padding: '15px 20px'
    },
    cardTitle: {
      fontSize: '1.2rem',
      fontWeight: '600',
      margin: 0
    },
    actionBtn: {
      background: 'rgba(255, 255, 255, 0.1)',
      color: 'white',
      border: `1px solid ${colors.borderDark}`,
      padding: '8px 15px',
      borderRadius: '8px',
      fontWeight: '600',
      fontSize: '0.85rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      backdropFilter: 'blur(10px)',
      display: 'flex',
      alignItems: 'center',
      gap: '6px'
    },
    addBtn: {
      background: `linear-gradient(135deg, ${colors.success}, #059669)`,
      color: 'white',
      border: 'none',
      padding: '8px 15px',
      borderRadius: '8px',
      fontWeight: '600',
      fontSize: '0.85rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      boxShadow: '0 4px 10px rgba(16, 185, 129, 0.3)'
    },
    loadingState: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '60px 20px',
      color: 'white',
      textAlign: 'center'
    },
    loadingText: {
      fontSize: '1.2rem',
      margin: '0 0 12px 0',
      fontWeight: '600',
      background: 'linear-gradient(135deg, #fff 0%, #a5b4fc 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text'
    },
    loadingSubtext: {
      color: colors.textLight,
      margin: 0,
      fontSize: '0.9rem'
    },
    spinner: {
      width: '18px',
      height: '18px',
      border: '2px solid transparent',
      borderTop: '2px solid white',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    },
    // FIXED: Better table container for full width display
    tableContainer: {
      overflowX: 'auto',
      width: '100%',
      WebkitOverflowScrolling: 'touch'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      minWidth: '1800px' // Increased to ensure all columns show
    },
    tableHeader: {
      background: 'rgba(255, 255, 255, 0.1)',
      color: 'white',
      borderBottom: `1px solid ${colors.borderDark}`,
      padding: '10px 8px',
      fontWeight: '600',
      whiteSpace: 'nowrap',
      fontSize: '0.75rem',
      textAlign: 'left',
      minWidth: '60px' // Ensure headers have minimum width
    },
    tableRow: {
      borderBottom: '1px solid rgba(255, 255, 255, 0.03)',
      color: 'white'
    },
    tableCell: {
      padding: '10px 8px',
      verticalAlign: 'middle',
      fontSize: '0.75rem',
      whiteSpace: 'nowrap'
    },
    emptyState: {
      padding: '30px 20px',
      textAlign: 'center'
    },
    emptyIcon: {
      color: `${colors.textLight}20`,
      marginBottom: '8px'
    },
    emptyTitle: {
      color: colors.textLight,
      marginBottom: '8px',
      fontSize: '0.9rem'
    },
    emptyText: {
      color: colors.textLight,
      fontSize: '0.8rem'
    },
    statusDropdown: {
      width: '100%',
      minWidth: '120px',
      padding: '6px 8px',
      background: 'rgba(255, 255, 255, 0.1)',
      border: `1px solid ${colors.borderDark}`,
      borderRadius: '4px',
      color: 'white',
      fontSize: '0.7rem',
      cursor: 'pointer',
      appearance: 'none',
      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='white' viewBox='0 0 16 16'%3E%3Cpath d='M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3E%3C/svg%3E")`,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'right 8px center',
      backgroundSize: '10px',
      outline: 'none'
    },
    statusOption: {
      background: colors.backgroundMedium,
      color: 'white',
      padding: '8px'
    },
    updatingSpinner: {
      display: 'inline-block',
      width: '12px',
      height: '12px',
      border: '2px solid transparent',
      borderTop: '2px solid white',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }
  };

  useEffect(() => {
    fetchEditorTasks();
  }, []);

  const fetchEditorTasks = async () => {
    try {
      setLoading(true);
      setMessage('');
      
      const editorId = sessionStorage.getItem('userId');
      
      if (!editorId) {
        throw new Error('Editor ID not found. Please login again.');
      }

      const response = await fetch(`${API_BASE}/get_editor_tasks.php?editor_id=${editorId}`);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      if (data.success) {
        let tasksData = [];
        
        if (data.data && Array.isArray(data.data.tasks)) {
          tasksData = data.data.tasks;
        } else if (Array.isArray(data.data)) {
          tasksData = data.data;
        } else if (Array.isArray(data.tasks)) {
          tasksData = data.tasks;
        } else {
          throw new Error('Invalid tasks data format received from server');
        }

        console.log('Fetched tasks:', tasksData); // Debug log
        setTasks(tasksData);
      } else {
        throw new Error(data.message || 'Failed to fetch tasks');
      }
      
    } catch (error) {
      console.error('❌ Error fetching editor tasks:', error);
      setMessage('❌ Error loading tasks: ' + error.message);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const isLinkAvailable = (link) => {
    return link && link !== '-' && link !== 'NULL' && link !== 'null' && link.trim() !== '';
  };

  const handleOpenLink = (link, linkName) => {
    if (link && link !== '-' && link !== 'NULL' && link !== 'null') {
      let formattedLink = link;
      if (!formattedLink.startsWith('http://') && !formattedLink.startsWith('https://')) {
        formattedLink = 'https://' + formattedLink;
      }
      window.open(formattedLink, '_blank', 'noopener,noreferrer');
    } else {
      setMessage(`❌ No ${linkName} link available`);
    }
  };

  const handleWorkOnTask = (task) => {
    navigate(`/submit-video?task_id=${task.id}`, { state: { task } });
  };

  const handleVideoNameClick = (task) => {
    navigate('/task-details', { 
      state: { task } 
    });
  };

  const handleClientClick = (clientId, clientName) => {
    navigate('/client-tasks', { 
      state: { clientId, clientName } 
    });
  };

  // Add this function to navigate to create task screen
  const handleAddNewTask = () => {
    navigate('/add-task');
  };

  // Function to handle status change
  const handleStatusChange = async (taskId, newStatus) => {
    try {
      // Set updating state for this specific task
      setUpdatingStatus(prev => ({ ...prev, [taskId]: true }));
      
      const editorId = sessionStorage.getItem('userId');
      
      const response = await fetch(`${API_BASE}/update_task_status.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          task_id: taskId,
          status: newStatus,
          editor_id: editorId,
          updated_at: new Date().toISOString()
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Update local state
        setTasks(prevTasks => 
          prevTasks.map(task => 
            task.id === taskId 
              ? { ...task, status: newStatus, display_status: newStatus }
              : task
          )
        );
        
        setMessage('✅ Status updated successfully!');
        
        // Refresh tasks after a delay
        setTimeout(() => {
          fetchEditorTasks();
        }, 1000);
      } else {
        throw new Error(data.message || 'Failed to update status');
      }
    } catch (error) {
      console.error('❌ Error updating status:', error);
      setMessage(`❌ Error updating status: ${error.message}`);
    } finally {
      // Clear updating state
      setUpdatingStatus(prev => ({ ...prev, [taskId]: false }));
    }
  };

  // Compact status badge - Updated to show dropdown instead
  const getStatusDisplay = (task) => {
    const isUpdating = updatingStatus[task.id];
    const currentStatus = task.status || task.display_status || 'pending';
    
    return (
      <select
        value={currentStatus}
        onChange={(e) => handleStatusChange(task.id, e.target.value)}
        style={styles.statusDropdown}
        disabled={isUpdating}
      >
        <option value="in_progress" style={styles.statusOption}>🔄 In Progress</option>
        <option value="completed" style={styles.statusOption}>✅ Completed</option>
        <option value="pending" style={styles.statusOption}>⏳ Pending</option>
      </select>
    );
  };

  // Compact action button
  const getActionButton = (task) => {
    const status = task.status || task.display_status;
    const hasEditedVideo = isLinkAvailable(task.edited_video_link);

    if ((status === 'completed' || status === 'approved') && hasEditedVideo) {
      return (
        <span style={{
          background: colors.success,
          color: 'white',
          padding: '5px 10px',
          borderRadius: '4px',
          fontSize: '0.7rem',
          fontWeight: '600',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px'
        }}>
          <i className="fas fa-check-double" style={{fontSize: '0.65rem'}}></i>
          Done
        </span>
      );
    }
    
    if ((status === 'completed' || status === 'approved') && !hasEditedVideo) {
      return (
        <button
          style={{
            background: colors.primary,
            color: 'white',
            border: 'none',
            padding: '5px 10px',
            borderRadius: '4px',
            fontSize: '0.7rem',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px'
          }}
          onClick={() => handleWorkOnTask(task)}
        >
          <i className="fas fa-edit" style={{fontSize: '0.65rem'}}></i>
          Work
        </button>
      );
    }
    
    if (status === 'needs_revision') {
      return (
        <button
          style={{
            background: colors.warning,
            color: 'white',
            border: 'none',
            padding: '5px 10px',
            borderRadius: '4px',
            fontSize: '0.7rem',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px'
          }}
          onClick={() => handleWorkOnTask(task)}
        >
          <i className="fas fa-sync" style={{fontSize: '0.65rem'}}></i>
          Revise
        </button>
      );
    }
    
    return (
      <button
        style={{
          background: colors.primary,
          color: 'white',
          border: 'none',
          padding: '5px 10px',
          borderRadius: '4px',
          fontSize: '0.7rem',
          fontWeight: '600',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px'
        }}
        onClick={() => handleWorkOnTask(task)}
      >
        <i className="fas fa-edit" style={{fontSize: '0.65rem'}}></i>
        Work
      </button>
    );
  };

  // Compact date format
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: '2-digit'
      });
    } catch (e) {
      return '-';
    }
  };
  
  // Text truncation with better handling
  const truncateText = (text, maxLength = 12) => {
    if (!text || text === '-' || text === 'NULL' || text === 'null' || text.trim() === '') return '-';
    const cleanText = String(text).trim();
    if (cleanText.length <= maxLength) return cleanText;
    return cleanText.substring(0, maxLength) + '..';
  };

  // FIXED: Better designer name display with fallback
  const getDesignerName = (task) => {
    // Check multiple possible fields for designer name
    const designerName = task.designer_name || task.designer_name || 'Unassigned';
    return designerName === 'Unassigned' ? 'Not Assigned' : designerName;
  };

  if (loading) {
    return (
      <Layout>
        <div style={styles.container}>
          <div style={styles.loadingState}>
            <div style={styles.spinner}></div>
            <h3 style={styles.loadingText}>Loading Your Tasks</h3>
            <p style={styles.loadingSubtext}>Please wait while we fetch your assigned tasks</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={styles.container}>
        {/* Header Section */}
        <div style={styles.header}>
          <div style={styles.headerContent}>
            <div>
              <div style={styles.greeting}>
                <h1 style={styles.pageTitle}>
                  <i className="fas fa-tasks" style={{marginRight: '6px'}}></i>
                  My Tasks
                </h1>
                <div style={styles.welcomeBadge}>
                  <span style={styles.badgeText}>Task Management</span>
                  <span style={styles.badgeRole}>Editor</span>
                </div>
              </div>
              <p style={styles.pageSubtitle}>
                Tasks assigned to you for editing
                {tasks.length > 0 && (
                  <span style={{
                    background: colors.primary, 
                    color: 'white', 
                    padding: '3px 6px', 
                    borderRadius: '3px',
                    marginLeft: '6px',
                    fontSize: '0.7rem',
                    fontWeight: '600'
                  }}>
                    {tasks.length} tasks
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Message Alert */}
        {message && (
          <div style={{
            ...styles.messageAlert,
            background: message.includes('✅') 
              ? 'rgba(16, 185, 129, 0.1)' 
              : 'rgba(220, 53, 69, 0.1)',
            border: message.includes('✅') 
              ? `1px solid ${colors.success}`
              : `1px solid ${colors.danger}`
          }}>
            <i className={`fas ${message.includes('✅') ? 'fa-check-circle' : 'fa-times-circle'}`} 
               style={{
                 fontSize: '0.8rem',
                 color: message.includes('✅') ? colors.success : colors.danger
               }}></i>
            <span style={{flex: 1}}>{message}</span>
            <button onClick={() => setMessage('')} style={{
              background: 'none', 
              border: 'none', 
              color: colors.danger, 
              cursor: 'pointer', 
              padding: '2px'
            }}>
              <i className="fas fa-times" style={{fontSize: '0.8rem'}}></i>
            </button>
          </div>
        )}

        {/* Main Content */}
        <div style={styles.content}>
          <div style={{display: 'flex', gap: '10px', marginBottom: '15px'}}>
            <button 
              style={styles.addBtn}
              onClick={handleAddNewTask}
            >
              <i className="fas fa-plus" style={{marginRight: '4px'}}></i>
              Add New Task
            </button>
            <button 
              style={styles.actionBtn}
              onClick={fetchEditorTasks}
              disabled={loading}
            >
              <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`} style={{marginRight: '4px'}}></i>
              Refresh
            </button>
          </div>

          {/* Tasks Card */}
          <div style={styles.formCard}>
            <div style={styles.cardHeader}>
              <h3 style={styles.cardTitle}>
                <i className="fas fa-list" style={{marginRight: '6px'}}></i>
                My Task List
              </h3>
            </div>
            <div style={{padding: '0'}}>
              {tasks.length === 0 ? (
                <div style={styles.emptyState}>
                  <i className="fas fa-tasks fa-2x" style={styles.emptyIcon}></i>
                  <h4 style={styles.emptyTitle}>No tasks assigned</h4>
                  <p style={styles.emptyText}>
                    You don't have any tasks assigned to you yet.
                  </p>
                </div>
              ) : (
                // FIXED: Better table container
                <div style={styles.tableContainer}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.tableHeader}>ID</th>
                        <th style={styles.tableHeader}>Video Name</th>
                        <th style={styles.tableHeader}>Client</th>
                        <th style={styles.tableHeader}>Operator</th>
                        <th style={styles.tableHeader}>Designer</th>
                        <th style={styles.tableHeader}>Type</th>
                        <th style={styles.tableHeader}>Count</th>
                        <th style={styles.tableHeader}>Raw Link</th>
                        <th style={styles.tableHeader}>Ref Link</th>
                        <th style={styles.tableHeader}>Notes</th>
                        <th style={styles.tableHeader}>D-Notes</th>
                        <th style={styles.tableHeader}>Status</th>
                        <th style={styles.tableHeader}>Edited</th>
                        <th style={styles.tableHeader}>Date</th>
                        <th style={styles.tableHeader}>Edit Link</th>
                        <th style={styles.tableHeader}>Thumbnail Link</th>
                        <th style={styles.tableHeader}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tasks.map((task) => {
                        const hasValidRawLink = isLinkAvailable(task.raw_link);
                        const hasValidRefLink = isLinkAvailable(task.reference_video_link);
                        const hasValidEditedLink = isLinkAvailable(task.edited_video_link);
                        const hasValidThumbnailLink = isLinkAvailable(task.thumbnail_link);
                        const isUpdating = updatingStatus[task.id];
                        
                        return (
                          <tr key={task.id} style={styles.tableRow}>
                            {/* ID - WHITE COLOR */}
                            <td style={{...styles.tableCell, fontWeight: 'bold', color: 'white', fontSize: '0.7rem'}}>
                              #{task.id}
                            </td>

                            {/* Video Name - WHITE COLOR */}
                            <td style={styles.tableCell}>
                              <button 
                                onClick={() => handleVideoNameClick(task)}
                                style={{ 
                                  color: 'white', // Changed to white
                                  fontWeight: '600', 
                                  textAlign: 'left',
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  padding: 0,
                                  fontSize: '0.75rem'
                                }}
                                title={task.video_name}
                              >
                                <i className="fas fa-video" style={{marginRight: '4px', color: 'white', fontSize: '0.65rem'}}></i>
                                {truncateText(task.video_name, 15)}
                              </button>
                            </td>

                            {/* Client - WHITE COLOR */}
                            <td style={styles.tableCell}>
                              <button 
                                onClick={() => handleClientClick(task.client_id, task.client_name)}
                                style={{ 
                                  color: 'white', // Changed to white
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  padding: 0,
                                  fontSize: '0.75rem'
                                }}
                                title={task.client_name}
                              >
                                {truncateText(task.client_name, 10)}
                              </button>
                            </td>

                            {/* Operator */}
                            <td style={styles.tableCell}>
                              <span style={{fontSize: '0.7rem', color: colors.textLight}} title={task.operator_name}>
                                {truncateText(task.operator_name, 8)}
                              </span>
                            </td>

                            {/* Designer - FIXED: Using proper designer name display */}
                            <td style={styles.tableCell}>
                              <span style={{fontSize: '0.7rem', color: colors.textLight}} title={getDesignerName(task)}>
                                {truncateText(getDesignerName(task), 10)}
                              </span>
                            </td>

                            {/* Video Type */}
                            <td style={styles.tableCell}>
                              <span style={{
                                background: colors.primary,
                                color: 'white',
                                padding: '3px 6px',
                                borderRadius: '3px',
                                fontSize: '0.65rem',
                                fontWeight: '600'
                              }}>
                                {truncateText(task.video_type, 8)}
                              </span>
                            </td>

                            {/* Video Count */}
                            <td style={styles.tableCell}>
                              <span style={{
                                background: colors.accent,
                                color: 'white',
                                padding: '3px 6px',
                                borderRadius: '3px',
                                fontSize: '0.65rem',
                                fontWeight: '600'
                              }}>
                                {task.video_count || 1}
                              </span>
                            </td>

                            {/* Raw Link - Icon Only */}
                            <td style={styles.tableCell}>
                              <button 
                                style={{
                                  background: hasValidRawLink ? colors.primary : 'rgba(255,255,255,0.1)',
                                  color: 'white',
                                  border: 'none',
                                  padding: '4px 6px',
                                  borderRadius: '3px',
                                  cursor: hasValidRawLink ? 'pointer' : 'not-allowed',
                                  opacity: hasValidRawLink ? 1 : 0.4,
                                  fontSize: '0.65rem'
                                }}
                                onClick={() => handleOpenLink(task.raw_link, 'raw video')}
                                disabled={!hasValidRawLink}
                                title={hasValidRawLink ? task.raw_link : 'No raw link'}
                              >
                                <i className="fas fa-external-link-alt"></i>
                              </button>
                            </td>

                            {/* Reference Link - Icon Only */}
                            <td style={styles.tableCell}>
                              <button 
                                style={{
                                  background: hasValidRefLink ? colors.accent : 'rgba(255,255,255,0.1)',
                                  color: 'white',
                                  border: 'none',
                                  padding: '4px 6px',
                                  borderRadius: '3px',
                                  cursor: hasValidRefLink ? 'pointer' : 'not-allowed',
                                  opacity: hasValidRefLink ? 1 : 0.4,
                                  fontSize: '0.65rem'
                                }}
                                onClick={() => handleOpenLink(task.reference_video_link, 'reference video')}
                                disabled={!hasValidRefLink}
                                title={hasValidRefLink ? task.reference_video_link : 'No reference link'}
                              >
                                <i className="fas fa-external-link-alt"></i>
                              </button>
                            </td>

                            {/* Notes */}
                            <td style={styles.tableCell} title={task.notes}>
                              <span style={{ color: colors.textLight, fontSize: '0.7rem' }}>
                                {truncateText(task.notes, 8)}
                              </span>
                            </td>

                            {/* Designer Notes */}
                            <td style={styles.tableCell} title={task.designer_notes}>
                              <span style={{ color: colors.textLight, fontSize: '0.7rem' }}>
                                {truncateText(task.designer_notes, 8)}
                              </span>
                            </td>

                            {/* Status - Now with dropdown */}
                            <td style={styles.tableCell}>
                              {isUpdating ? (
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  color: colors.textLight,
                                  fontSize: '0.7rem'
                                }}>
                                  <div style={styles.updatingSpinner}></div>
                                  Updating...
                                </div>
                              ) : (
                                getStatusDisplay(task)
                              )}
                            </td>

                            {/* Edited Status */}
                            <td style={styles.tableCell}>
                              <span style={{
                                background: hasValidEditedLink ? colors.success : colors.secondary,
                                color: 'white',
                                padding: '4px 6px',
                                borderRadius: '3px',
                                fontSize: '0.65rem',
                                fontWeight: '600'
                              }}>
                                {hasValidEditedLink ? 'Yes' : 'No'}
                              </span>
                            </td>

                            {/* Shoot Date */}
                            <td style={styles.tableCell}>
                              <span style={{fontSize: '0.7rem', color: colors.textLight}}>
                                {formatDate(task.shoot_date)}
                              </span>
                            </td>

                            {/* Edited Link - Icon Only */}
                            <td style={styles.tableCell}>
                              <button
                                style={{
                                  background: hasValidEditedLink ? colors.success : 'rgba(255,255,255,0.1)',
                                  color: 'white',
                                  border: 'none',
                                  padding: '4px 6px',
                                  borderRadius: '3px',
                                  cursor: hasValidEditedLink ? 'pointer' : 'not-allowed',
                                  opacity: hasValidEditedLink ? 1 : 0.4,
                                  fontSize: '0.65rem'
                                }}
                                onClick={() => handleOpenLink(task.edited_video_link, 'edited video')}
                                disabled={!hasValidEditedLink}
                                title={hasValidEditedLink ? task.edited_video_link : 'Not edited yet'}
                              >
                                <i className="fas fa-external-link-alt"></i>
                              </button>
                            </td>

                            {/* Thumbnail Link - Icon Only */}
                            <td style={styles.tableCell}>
                              <button
                                style={{
                                  background: hasValidThumbnailLink ? colors.warning : 'rgba(255,255,255,0.1)',
                                  color: 'white',
                                  border: 'none',
                                  padding: '4px 6px',
                                  borderRadius: '3px',
                                  cursor: hasValidThumbnailLink ? 'pointer' : 'not-allowed',
                                  opacity: hasValidThumbnailLink ? 1 : 0.4,
                                  fontSize: '0.65rem'
                                }}
                                onClick={() => handleOpenLink(task.thumbnail_link, 'thumbnail')}
                                disabled={!hasValidThumbnailLink}
                                title={hasValidThumbnailLink ? task.thumbnail_link : 'No thumbnail'}
                              >
                                <i className="fas fa-image"></i>
                              </button>
                            </td>

                            {/* Action */}
                            <td style={styles.tableCell}>
                              {getActionButton(task)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </Layout>
  );
}

export default MyTasks;